/**
 * governance/index.ts
 * Canonical public API for the governance system.
 *
 * ARCHITECTURE v4.0 — Pipeline is the canonical validation system.
 * Use runPipeline() / runPipelineBatch() for all validation.
 *
 * NOTE: Most consumers import directly from sub-modules (e.g.
 * governance/pipeline/orchestrator, governance/ldg/ldgBuilder).
 * This barrel provides a convenience API for the most common imports.
 * Wildcard re-exports (export *) removed in s35 — explicit named exports only.
 * Missing external-facing exports added in s37 — barrel is now complete public API.
 *
 * @see governance/pipeline/orchestrator.ts — canonical entry point
 * @see governance/pipeline/issueContract.ts — canonical issue type (ValidationIssue)
 */

// ─── Pipeline (canonical validation) ──────────────────────────────────────────
export { runPipeline, runPipelineBatch } from './pipeline/orchestrator'
export type { PipelineOptions } from './pipeline/orchestrator'
export type {
  GovernancePipelineResult,
  PipelineBatchResult,
  PipelineResult,
  ValidationIssue,
  GateVerdict,
  IssueDomain,
  IssueKind,
} from './pipeline/issueContract'
export {
  getIssueOwnershipLabel,
  resolveIssueOwnership,
} from './pipeline/issueOwnership'
export type { IssueOwner, IssueOwnership } from './pipeline/issueOwnership'

// Diagnostics: code frequency report (used by admin OverviewDashboard)
export { computeCodeFrequencyReport } from './pipeline/utils/codeFrequency'
export type {
  CodeFrequencyEntry,
  CodeFrequencyReport,
} from './pipeline/utils/codeFrequency'

// Diagnostics: pipeline snapshots (run-to-run diffing)
export {
  createSnapshot,
  diffSnapshots,
  listSnapshots,
  loadSnapshot,
  saveSnapshot,
  deleteSnapshot,
  clearAllSnapshots,
} from './pipeline/snapshots'
export type {
  PipelineSnapshot,
  SnapshotAggregate,
  SnapshotQuestionRow,
  SnapshotRunConfig,
  SnapshotDiff,
  SnapshotIndexEntry,
} from './pipeline/snapshots'

// ─── Policy Engine ────────────────────────────────────────────────────────────
// NOTE: stab-ronde 3 (mei 2026) — `getSeverityLevel`, `getMaxSentenceLength`
// verwijderd uit barrel (nul externe consumers). Beide blijven gedefinieerd in
// pipeline/policyEngine.ts: `getMaxSentenceLength` wordt intern aangeroepen
// vanuit utils/languageQuality.ts via dynamic require; `getSeverityLevel` is
// een 3-regel wrapper rond severityValue() en wordt nergens meer aangeroepen
// (kandidaat voor source-delete in een volgende ronde).
export {
  getPolicy,
  initializePolicy,
  isPlusTier,
  getBaseTierForPlus,
  familyRequiresVisual,
  isAssessmentEligibleType,
  getSkipStages,
  refreshStoredConfig,
  getNotationRequirements,
  getDistractorPattern,
  requiresRuleReference,
  getCheckStrategyMinTier,
  getPreferredStrategyKeywords,
  getPreferredQuestionTypes,
  // Phase 4: scoring policy access (sentence-length helper is internal-only)
  getScoringWeights,
  SEVERITY,
} from './pipeline/policyEngine'
export type { GovernancePolicy } from './pipeline/policyTypes'
export type { SeverityLevelName, ScoringWeights } from './pipeline/policyEngine'
export { DEFAULT_POLICY } from './pipeline/defaultPolicy'

// ─── Module Rulebooks (Phase 3) ───────────────────────────────────────────────
// NOTE: `getRulebookByShortCode` removed from barrel in RC-3 (no external consumer).
// It still exists in ./rulebooks for internal use; re-add here if a UI consumer appears.
// NOTE: stab-ronde 3 — `RULEBOOKS_REGISTRY`, `countModuleRulebooks`,
// `isQuestionTypeDiscouragedForFamily` verwijderd uit barrel: nul externe
// consumers. RULEBOOKS_REGISTRY wordt enkel intern in rulebooks/index.ts
// gebruikt; countModuleRulebooks via direct path-import in systemStats.ts;
// isQuestionTypeDiscouragedForFamily was bron-dood (bron-delete uitgevoerd).
// `resolveFamilyTypeHints` blijft in barrel: RulebookHintsPanel (admin)
// importeert het via deze route.
export {
  getModuleRulebook,
  listModuleRulebooks,
  getModuleRoadmap,
  countRoadmapEntries,
  resolveFamilyTypeHints,
  resolveFamilyVisualHints,
} from './rulebooks'
export type {
  ModuleRulebook,
  TierBand,
  ModuleDidactics,
  ModuleNotationRules,
  ModuleContentRules,
  ModuleExplanationRules,
  ModuleVisualRules,
  ModuleQuestionTypeRules,
  ModuleQuestionTypeEntry,
  ModuleDistractorTrap,
  QuestionTypeDidacticRole,
  FamilyTypeHint,
  ModuleRoadmap,
  FutureQuestionTypeEntry,
  FutureVisualEntry,
} from './rulebooks'

// ─── Governance Map Generator (auto-generates MAP from seeds) ─────────────────
// NOTE: stab-ronde 2 (mei 2026) — visualRegistrySync exports (`checkVisualRegistrySync`,
// `checkGovernanceMapStaleness`, `getRegisteredVisualTypes`) en `invalidateMapCache`
// zijn uit de barrel verwijderd: nul consumers. Functies leven nog in hun bron-
// bestanden voor potentieel diagnostic-gebruik.
// NOTE: `getGeneratedMapFamilyCount` was removed from barrel in RC-3;
// `getMapFamilyCount` was deleted entirely in RC-5 (zero consumers).
// Use `Object.keys(getGeneratedGovernanceMap().families).length` if needed.
export {
  generateGovernanceMapFromSeeds,
  getGeneratedGovernanceMap,
  invalidateGovernanceMapCache,
} from './pipeline/generateGovernanceMap'
export type { GeneratedVisualGovernanceMap } from './pipeline/generateGovernanceMap'

// ─── LDG (Learning Dependency Graph) ──────────────────────────────────────────
// NOTE: stab-ronde 2 — `getAncestors`, `getDescendants`, `isPrerequisiteOf`,
// `validateDAG`, `ALL_PREREQUISITES` verwijderd uit barrel (zero consumers).
// `validateDAG` en `ALL_PREREQUISITES` blijven beschikbaar via directe imports
// uit `./ldg/dagValidator` resp. `./ldg/prerequisites` voor interne pipeline-code.
export { buildLDG, calculateFrontier } from './ldg/ldgBuilder'
export { getLDG } from './ldg'
export type {
  LDGraph,
  LDGNode,
  LDGEdge,
  FrontierResult,
  FamilyMasteryInput,
  FrontierFamily,
} from './ldg/types'

// ─── Question Quality Engine ──────────────────────────────────────────────────
// NOTE: stab-ronde 2 — `questionQualityEngine` (namespace object) en
// `scoreQuestion` verwijderd uit barrel (nul externe consumers). `scoreQuestion`
// blijft intern in questionQualityEngine.ts als helper voor `buildQualityReport`.
export { buildQualityReport } from './questionQualityEngine'
export type {
  QuestionQualityScore,
  QuestionQualityReport,
  QualityDimension,
} from './questionQualityEngine'

// ─── Question Type Definitions ────────────────────────────────────────────────
// NOTE: stab-ronde 3 — `getQuestionTypeDefinition`, `getQuestionTypesByCategory`,
// `getQuestionTypesRequiringVisual`, `validateQuestionType` verwijderd uit
// barrel én bron: nul callers anywhere in src/. QUESTION_TYPE_REGISTRY blijft
// als SSOT-data export; `getDok3CapableTypes` en `getTypesRequiringStructuredData`
// blijven in bron (intern gebruikt door pedagogy/diversity stages, niet barrel-
// geëxposeerd).
export {
  QUESTION_TYPE_REGISTRY,
  type QuestionTypeDefinition,
} from './questionTypeDefinitions'

// ─── Cognitive & Didactic Analysis ────────────────────────────────────────────
// NOTE: `cognitiveLevelColor` + `computeCognitiveLevelDistribution` removed from
// barrel in RC-3 (no external consumers).
// NOTE: stab-ronde 2 — `cognitiveLevelLabel`, `cognitiveLevelColor`,
// `computeCognitiveLevelDistribution` verwijderd uit bron (nul callers).
// NOTE: stab-ronde 3 — `assessExplanationDepth`, `assessDistractorDepth`
// verwijderd uit barrel: enige consumer is questionQualityEngine.ts (intern,
// via direct import van './didacticDepth'). Functies blijven in bron.
export { detectCognitiveLevel, COGNITIVE_LEVEL_RANK } from './cognitiveLevel'
export type { CognitiveLevel } from './cognitiveLevel'

// ─── Tier Bands (SSOT for tier-band thresholds and DOK expectations) ──────────
// Re-exported so cross-domain consumers (e.g. mastery/selection) can import
// from the governance barrel rather than deep-importing into tierBands.ts.
// Audit reference: GSR-22 (Governance Refactor 2026 — PR-8).
export {
  MIN_DOK_BY_TIER_BAND,
  MAX_DOK_BY_TIER_BAND,
  getTierBand as getGovernanceTierBand,
} from './tierBands'

// ─── System Stats ─────────────────────────────────────────────────────────────
// NOTE: stab-ronde 3 — `computeModuleStats` en `computeCalibrationStats`
// verwijderd uit barrel én bron (nul callers). De 180-regel
// computeCalibrationStats-functie was sinds RC-8 niet meer aangeroepen in de
// admin dashboard; ook de comment in useDashboardData.ts is bijgewerkt.
export { computeLiveSystemStats } from './systemStats'
export type { GovernanceSystemStats } from './systemStats'

// ─── Governance Config Store ──────────────────────────────────────────────────
export {
  loadGovernanceConfig,
  saveGovernanceConfig,
  resetGovernanceConfig,
  getDefaultGovernanceConfig,
} from './pipeline/governanceConfigStore'
export type {
  GovernanceConfigOverrides,
  EnforcementMode,
  GraceLedgerEntry,
} from './pipeline/governanceConfigStore'

// ─── Shadow-mode Telemetry (FG4 §F0.B) ────────────────────────────────────────
// Shadow findings store + impact-report builder. Surfaces "would-block"
// counts before any enforcement flip — feeds the admin Shadow Impact card.
export {
  recordShadowFinding,
  getShadowFindings,
  clearShadowFindings,
  subscribeShadowFindings,
  buildShadowImpactReport,
} from './shadow/shadowFindings'
export type {
  ShadowFinding,
  ShadowSource,
  ShadowImpactReport,
} from './shadow/shadowFindings'

// ─── Shared Utilities ─────────────────────────────────────────────────────────
// NOTE: stab-ronde 2 — `SEVERITY_ORDER`, `compareSeverity`, `isBlockingSeverity`
// verwijderd uit barrel (nul consumers). Definities leven nog in utils/severity.ts
// als interne hulpfuncties.
export type { SeverityLevel } from './utils'
export {
  severityLabel,
  severityColor,
  deriveDisplaySeverity,
  // Severity/gate threshold SSOT (detangle §10.5) — consumed by admin
  // usePipelineRun so its gate-defaults mirror the canonical numbers.
  CRITICAL_BLOCKER_SEVERITY_THRESHOLD,
  REVIEW_COACH_SEVERITY_THRESHOLD,
  isGenericText,
  GENERIC_EXPLANATION_PATTERNS,
  GENERIC_DISTRACTOR_PATTERNS,
} from './utils'
export {
  analyzeNumberPatterns,
  detectNumberPatternCollapse,
} from './utils/numberPatternAnalysis'
export type { NumberPatternAnalysis } from './utils/numberPatternAnalysis'
export {
  analyzeLinguisticVariation,
  detectLinguisticTemplateCollapse,
  getLinguisticVariationSuggestions,
} from './utils/linguisticVariationAnalysis'
export type { LinguisticVariationAnalysis } from './utils/linguisticVariationAnalysis'

// ─── Pedagogical Contract (PEDAGOGICAL_CONTRACT_SPEC) ─────────────────────────
export { runPedagogyStage } from './pipeline/stages/pedagogyStage'
export { estimateDok } from './utils/dokAutoTagger'
export type { DokEstimate } from './utils/dokAutoTagger'
export {
  getMisconception,
  isKnownMisconception,
  getMisconceptionsForModule,
  getAllMisconceptionIds,
  MISCONCEPTION_REGISTRY,
} from './data/misconceptions/index'
export type { MisconceptionDefinition } from './data/misconceptions/index'

// ─── SSOT Registry (data-driven catalog of all single-sources-of-truth) ───────
// Each entry reads its values LIVE via the SSOT's public accessors — surfaced
// in the admin portal for insight. Adding a new SSOT? Register it once here.
export {
  getSsotRegistry,
  getSsotEntry,
  SSOT_REGISTRY_COUNT,
} from './ssotRegistry'
export type {
  SsotEntry,
  SsotDomain,
  SsotSnapshot,
  SsotColumn,
} from './ssotRegistry'
